import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import {
  Shield, ShieldAlert, Plus, Search, Upload, Download, RefreshCw,
  Loader2, AlertTriangle, Check, Clock, Pause, X, Eye,
  TrendingUp, Activity, CreditCard, ChevronRight, FileDown, FileText,
  Edit2, Trash2, Play, Info, Bell, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';
import { generateDutyRiskPdf } from '../utils/pdfGenerator';
import { apiGet, apiPost, apiPut, apiDelete, API_URL } from '../lib/apiClient';

const STATUS_CONFIG = {
  no_change: { label: 'No Change', color: 'bg-emerald-100 text-emerald-700', icon: Check },
  change_detected: { label: 'Change Detected', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  needs_input: { label: 'Needs Input', color: 'bg-amber-100 text-amber-700', icon: Info },
  no_data_available: { label: 'No Data', color: 'bg-slate-100 text-slate-500', icon: Search },
  paused_insufficient_credits: { label: 'Paused', color: 'bg-orange-100 text-orange-700', icon: Pause },
  error: { label: 'Error', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  pending: { label: 'Pending', color: 'bg-blue-100 text-blue-700', icon: Clock },
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" /> {config.label}
    </span>
  );
};

const DutyRiskMonitoring = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('watchlist');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailAlerts, setDetailAlerts] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [runningCheck, setRunningCheck] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Add form state
  const [formData, setFormData] = useState({
    item_name: '', hs_code: '', origin_country: '',
    unit_value: '', annual_volume: '', currency: 'EUR', sku: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [dashData, listData, alertData] = await Promise.all([
        apiGet('/api/duty-monitoring/dashboard').catch(() => null),
        apiGet('/api/duty-monitoring/watchlist').catch(() => null),
        apiGet('/api/duty-monitoring/alerts?limit=50').catch(() => null),
      ]);
      if (dashData) setDashboard(dashData);
      if (listData) setWatchlist(listData.items || []);
      if (alertData) setAlerts(alertData.alerts || []);
    } catch (e) { toast.error('Failed to load monitoring data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const exportPdf = async () => {
    try {
      const pdf = await generateDutyRiskPdf(watchlist, alerts, dashboard);
      pdf.save(`duty_risk_monitoring_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported');
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!formData.item_name || !formData.hs_code || !formData.origin_country) {
      toast.error('Item name, HS code, and origin country are required');
      return;
    }
    setFormLoading(true);
    try {
      const body = {
        item_name: formData.item_name,
        hs_code: formData.hs_code,
        origin_country: formData.origin_country,
        import_market: 'EU',
        currency: formData.currency || 'EUR',
        sku: formData.sku || null,
        unit_value: formData.unit_value ? parseFloat(formData.unit_value) : null,
        annual_volume: formData.annual_volume ? parseInt(formData.annual_volume) : null,
      };
      await apiPost('/api/duty-monitoring/watchlist', body);
      toast.success(`Added "${formData.item_name}" to watchlist`);
      setFormData({ item_name: '', hs_code: '', origin_country: '', unit_value: '', annual_volume: '', currency: 'EUR', sku: '' });
      setShowAddForm(false);
      fetchAll();
    } catch (e) { toast.error(e?.message || 'Failed to add item'); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (itemId, name) => {
    if (!window.confirm(`Remove "${name}" from watchlist?`)) return;
    try {
      await apiDelete(`/api/duty-monitoring/watchlist/${itemId}`);
      toast.success('Item removed');
      fetchAll();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleRunAll = async () => {
    setRunningCheck(true);
    try {
      const data = await apiPost('/api/duty-monitoring/run-all');
      toast.success(`Checked ${data.processed} items. ${data.changes} changes detected.`);
      fetchAll();
    } catch (e) { toast.error(e?.message || 'Check failed'); }
    finally { setRunningCheck(false); }
  };

  const handleCheckSingle = async (itemId) => {
    try {
      await apiPost(`/api/duty-monitoring/watchlist/${itemId}/check`);
      toast.success('Check complete');
      fetchAll();
    } catch (e) { toast.error(e?.message || 'Check failed'); }
  };

  const handleViewDetail = async (itemId) => {
    try {
      const data = await apiGet(`/api/duty-monitoring/watchlist/${itemId}`);
      setDetailItem(data.item);
      setDetailAlerts(data.alerts || []);
      setActiveTab('detail');
    } catch (e) { toast.error('Failed to load item detail'); }
  };

  const handleUpdateItem = async () => {
    if (!editItem) return;
    try {
      await apiPut(`/api/duty-monitoring/watchlist/${editItem.item_id}`, {
        item_name: editItem.item_name,
        unit_value: editItem.unit_value ? parseFloat(editItem.unit_value) : null,
        annual_volume: editItem.annual_volume ? parseInt(editItem.annual_volume) : null,
        currency: editItem.currency,
      });
      toast.success('Item updated');
      setEditItem(null);
      fetchAll();
      if (detailItem?.item_id === editItem.item_id) handleViewDetail(editItem.item_id);
    } catch (e) { toast.error('Update failed'); }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const data = await apiPost('/api/duty-monitoring/import-csv', fd);
      toast.success(`Imported ${data.imported} items. ${data.skipped} skipped. ${data.duplicates} duplicates.`);
      if (data.errors?.length > 0) toast.warning(`${data.errors.length} errors: ${data.errors[0]}`);
      setShowImport(false);
      fetchAll();
    } catch (e) { toast.error('Import failed'); }
  };

  const handleSimulateChange = async () => {
    try {
      const data = await apiPost('/api/duty-monitoring/simulate-change');
      toast.success(`Simulated duty change on "${data.item_name}": ${data.alert.duty_before}% → ${data.alert.duty_after}%`);
      fetchAll();
    } catch (e) { toast.error(e?.message || 'Simulation failed'); }
  };

  const filteredWatchlist = watchlist.filter(item => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return item.item_name?.toLowerCase().includes(q) ||
      item.user_entered_code?.includes(q) ||
      item.origin_country?.toLowerCase().includes(q);
  });

  if (loading) return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar user={user} setUser={setUser} />
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E40AF]" />
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar user={user} setUser={setUser} />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6 pt-20 lg:pt-24">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <ShieldAlert className="w-5 h-5 text-[#1E40AF]" />
              <h1 className="text-xl font-bold text-slate-800" data-testid="duty-risk-title">Duty Risk Monitoring</h1>
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-semibold rounded-full">EU</span>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500 ml-8">Monitor HS codes and trade routes for duty changes impacting cost and compliance.</p>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={exportPdf} className="text-xs border-slate-200 h-8" data-testid="export-duty-pdf-btn">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> Export PDF
                </Button>
                <Button variant="outline" size="sm" onClick={handleSimulateChange} className="text-xs border-slate-200 h-8" data-testid="simulate-change-btn">
                  <Activity className="w-3.5 h-3.5 mr-1.5" /> Simulate Change
                </Button>
                <Button variant="outline" size="sm" onClick={handleRunAll} disabled={runningCheck || watchlist.length === 0} className="border-[#1E40AF]/30 text-[#1E40AF] h-8" data-testid="run-all-btn">
                  {runningCheck ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                  Run All Checks
                </Button>
              </div>
            </div>
          </div>

          {/* KPI Tiles */}
          <div className="grid grid-cols-4 gap-3 mb-6" data-testid="kpi-tiles">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Items Monitored</span>
                <Shield className="w-4 h-4 text-[#1E40AF]" />
              </div>
              <p className="text-xl font-bold text-slate-800" data-testid="kpi-items">{dashboard?.active_items || 0}</p>
              <p className="text-xs text-slate-400">{dashboard?.total_items || 0} total</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Changes (30d)</span>
                <AlertTriangle className="w-4 h-4 text-red-500" />
              </div>
              <p className="text-xl font-bold text-slate-800" data-testid="kpi-changes">{dashboard?.changes_detected_30d || 0}</p>
              <p className="text-xs text-slate-400">duty/tariff changes</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Cost Impact</span>
                <TrendingUp className="w-4 h-4 text-amber-500" />
              </div>
              <p className="text-xl font-bold text-slate-800" data-testid="kpi-impact">
                {dashboard?.total_impact_30d ? `€${dashboard.total_impact_30d.toLocaleString()}` : '€0'}
              </p>
              <p className="text-xs text-slate-400">estimated exposure</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Credits Used</span>
                <CreditCard className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-xl font-bold text-slate-800" data-testid="kpi-credits">{dashboard?.monitoring_credits_used || 0}</p>
              <p className="text-xs text-slate-400">this month</p>
            </div>
          </div>

          {/* Data Pipeline Status */}
          {dashboard?.data_pipeline && (
            <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex items-center justify-between" data-testid="data-pipeline-bar">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${dashboard.data_pipeline.status === 'active' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
                  <span className="text-xs font-medium text-slate-600">Live Data Pipeline</span>
                </div>
                <div className="h-4 w-px bg-slate-200" />
                <span className="text-xs text-slate-500">
                  <span className="font-medium text-emerald-600">{dashboard.data_pipeline.live_api_measures || 0}</span> live
                  {' / '}
                  <span className="font-medium text-blue-600">{dashboard.data_pipeline.static_measures || 0}</span> static
                  {' / '}
                  <span className="font-medium text-slate-600">{dashboard.data_pipeline.cached_measures || 0}</span> cached
                </span>
                {dashboard.data_pipeline.last_refresh && (
                  <>
                    <div className="h-4 w-px bg-slate-200" />
                    <span className="text-xs text-slate-400">
                      Last refresh: {new Date(dashboard.data_pipeline.last_refresh).toLocaleString()}
                    </span>
                  </>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7 border-slate-200"
                onClick={async () => {
                  try {
                    const d = await apiPost('/api/duty-monitoring/refresh-cache');
                    toast.success(d.message);
                    fetchAll();
                  } catch { toast.error('Refresh failed'); }
                }}
                data-testid="refresh-cache-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh All
              </Button>
            </div>
          )}

          {/* Tab navigation */}
          <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
            {[
              { id: 'watchlist', label: 'Watchlist', icon: Shield },
              { id: 'alerts', label: `Alerts${alerts.filter(a => !a.read).length ? ` (${alerts.filter(a => !a.read).length})` : ''}`, icon: Bell },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setDetailItem(null); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id || (tab.id === 'watchlist' && activeTab === 'detail')
                    ? 'border-[#1E40AF] text-[#1E40AF]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </div>

          {/* WATCHLIST TAB */}
          {(activeTab === 'watchlist' && !detailItem) && (
            <div>
              {/* Actions bar */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search items..."
                      className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20 w-64"
                      data-testid="watchlist-search"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={`${API_URL}/api/duty-monitoring/csv-template`} download>
                    <Button variant="outline" size="sm" className="text-xs" data-testid="download-template-btn">
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Template
                    </Button>
                  </a>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowImport(true)} data-testid="import-csv-btn">
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Import CSV
                  </Button>
                  <Button size="sm" className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-xs" onClick={() => setShowAddForm(true)} data-testid="add-item-btn">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Item
                  </Button>
                </div>
              </div>

              {/* Import CSV modal */}
              {showImport && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
                  <div className="bg-white rounded-2xl p-6 w-[480px] shadow-xl">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-800">Import Watchlist CSV</h3>
                      <button onClick={() => setShowImport(false)}><X className="w-5 h-5 text-slate-400" /></button>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">Upload a CSV with columns: item_name, hs_code, origin_country, unit_value, annual_volume, currency, sku</p>
                    <a href={`${API_URL}/api/duty-monitoring/csv-template`} download className="text-xs text-[#1E40AF] underline mb-4 block">Download template</a>
                    <input type="file" accept=".csv" onChange={handleImportCSV} className="text-sm" data-testid="import-file-input" />
                  </div>
                </div>
              )}

              {/* Add form modal */}
              {showAddForm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
                  <div className="bg-white rounded-2xl p-6 w-[520px] shadow-xl">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="text-lg font-semibold text-slate-800">Add Watchlist Item</h3>
                      <button onClick={() => setShowAddForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
                    </div>
                    <form onSubmit={handleAddItem} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">Item Name *</label>
                          <input value={formData.item_name} onChange={e => setFormData({...formData, item_name: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20" placeholder="e.g., Steel Pipe Fitting" data-testid="form-item-name" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">HS Code *</label>
                          <input value={formData.hs_code} onChange={e => setFormData({...formData, hs_code: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20" placeholder="e.g., 730431" data-testid="form-hs-code" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">Origin Country *</label>
                          <input value={formData.origin_country} onChange={e => setFormData({...formData, origin_country: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20" placeholder="e.g., CN" maxLength={2} data-testid="form-origin" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">Import Market</label>
                          <input value="EU" disabled className="w-full px-3 py-2 text-sm border border-slate-100 rounded-lg bg-slate-50 text-slate-400" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">Unit Value</label>
                          <input type="number" step="0.01" value={formData.unit_value} onChange={e => setFormData({...formData, unit_value: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20" placeholder="e.g., 12.50" data-testid="form-unit-value" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">Annual Volume</label>
                          <input type="number" value={formData.annual_volume} onChange={e => setFormData({...formData, annual_volume: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20" placeholder="e.g., 5000" data-testid="form-volume" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">Currency</label>
                          <select value={formData.currency} onChange={e => setFormData({...formData, currency: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-currency">
                            <option value="EUR">EUR</option>
                            <option value="USD">USD</option>
                            <option value="GBP">GBP</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">SKU</label>
                          <input value={formData.sku} onChange={e => setFormData({...formData, sku: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20" placeholder="Optional" data-testid="form-sku" />
                        </div>
                      </div>
                      {!formData.unit_value && !formData.annual_volume && (
                        <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg flex items-center gap-2">
                          <Info className="w-3.5 h-3.5 shrink-0" /> Add volume and value to estimate financial impact.
                        </p>
                      )}
                      <div className="flex justify-end gap-3 pt-2">
                        <Button variant="outline" type="button" onClick={() => setShowAddForm(false)}>Cancel</Button>
                        <Button type="submit" disabled={formLoading} className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white" data-testid="form-submit">
                          {formLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                          Add to Watchlist (5 credits)
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Watchlist table */}
              {filteredWatchlist.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                  <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Watchlist Items</h3>
                  <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">Start monitoring duty changes by adding HS codes and trade routes to your watchlist.</p>
                  <Button className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white" onClick={() => setShowAddForm(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add Your First Item
                  </Button>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="watchlist-table">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">HS Code</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Route</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Last Checked</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Duty</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Impact</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWatchlist.map(item => (
                        <tr key={item.item_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`watchlist-row-${item.item_id}`}>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-slate-700">{item.item_name}</p>
                            {item.sku && <p className="text-xs text-slate-400">{item.sku}</p>}
                          </td>
                          <td className="px-4 py-3">
                            <code className="text-sm text-[#1E40AF] bg-blue-50 px-2 py-0.5 rounded font-mono">{item.user_entered_code}</code>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {item.origin_country} <ChevronRight className="w-3 h-3 inline text-slate-300" /> {item.import_market}
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                          <td className="px-4 py-3 text-xs text-slate-400">
                            {item.last_checked_at ? new Date(item.last_checked_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-700">
                            {item.last_known_duty_value != null ? `${item.last_known_duty_value}%` : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {item.last_known_measure?.source && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                item.last_known_measure.source === 'uk_trade_tariff_api' ? 'bg-emerald-100 text-emerald-700' :
                                item.last_known_measure.source === 'cached' ? 'bg-blue-100 text-blue-700' :
                                'bg-slate-100 text-slate-500'
                              }`} data-testid={`source-${item.item_id}`}>
                                {item.last_known_measure.source === 'uk_trade_tariff_api' ? 'Live API' :
                                 item.last_known_measure.source === 'cached' ? 'Cached' :
                                 item.last_known_measure.source === 'static_eu_taric' ? 'EU Static' : 'Fallback'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {item.estimated_annual_impact != null ? (
                              <span className="font-medium text-slate-700">€{item.estimated_annual_impact.toLocaleString()}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => handleViewDetail(item.item_id)} className="p-1.5 text-slate-400 hover:text-[#1E40AF] hover:bg-blue-50 rounded-lg" title="View detail" data-testid={`view-${item.item_id}`}>
                                <Eye className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleCheckSingle(item.item_id)} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Check now">
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDelete(item.item_id, item.item_name)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title="Remove" data-testid={`delete-${item.item_id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ITEM DETAIL VIEW */}
          {activeTab === 'detail' && detailItem && (
            <div>
              <button onClick={() => { setActiveTab('watchlist'); setDetailItem(null); }} className="flex items-center gap-2 text-sm text-[#1E40AF] hover:underline mb-4" data-testid="back-to-watchlist">
                <ChevronRight className="w-4 h-4 rotate-180" /> Back to Watchlist
              </button>

              <div className="grid grid-cols-3 gap-6">
                {/* Item info */}
                <div className="col-span-2 space-y-6">
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-800">{detailItem.item_name}</h3>
                      <StatusBadge status={detailItem.status} />
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-slate-400 text-xs mb-1">HS Code</p>
                        <code className="text-[#1E40AF] bg-blue-50 px-2 py-0.5 rounded font-mono">{detailItem.user_entered_code}</code>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs mb-1">Trade Route</p>
                        <p className="text-slate-700">{detailItem.origin_country} → {detailItem.import_market}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs mb-1">Current Duty</p>
                        <p className="text-slate-700 font-semibold">{detailItem.last_known_duty_value != null ? `${detailItem.last_known_duty_value}%` : 'N/A'}</p>
                      </div>
                      {detailItem.last_known_measure && (
                        <>
                          <div>
                            <p className="text-slate-400 text-xs mb-1">Measure Type</p>
                            <p className="text-slate-600">{detailItem.last_known_measure.measure_type}</p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-xs mb-1">Legal Reference</p>
                            <p className="text-slate-600 text-xs">{detailItem.last_known_measure.legal_reference}</p>
                          </div>
                          {detailItem.last_known_measure.safeguard_rate > 0 && (
                            <div>
                              <p className="text-slate-400 text-xs mb-1">Safeguard</p>
                              <p className="text-red-600 font-medium">{detailItem.last_known_measure.safeguard_rate}%</p>
                            </div>
                          )}
                          <div>
                            <p className="text-slate-400 text-xs mb-1">Data Source</p>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              detailItem.last_known_measure.source === 'uk_trade_tariff_api' ? 'bg-emerald-100 text-emerald-700' :
                              detailItem.last_known_measure.source === 'cached' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-500'
                            }`} data-testid="detail-data-source">
                              {detailItem.last_known_measure.source === 'uk_trade_tariff_api' ? 'UK Trade Tariff API (Live)' :
                               detailItem.last_known_measure.source === 'cached' ? 'Cached (Live)' :
                               detailItem.last_known_measure.source === 'static_eu_taric' ? 'EU TARIC Reference' : 'Static Fallback'}
                            </span>
                            {detailItem.last_known_measure.fetched_at && (
                              <p className="text-[10px] text-slate-400 mt-0.5">Fetched: {new Date(detailItem.last_known_measure.fetched_at).toLocaleString()}</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Financial info */}
                    <div className="mt-6 pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-400 text-xs mb-1">Unit Value</p>
                          <p className="text-slate-700">{detailItem.unit_value ? `€${detailItem.unit_value}` : <span className="text-amber-500">Not set</span>}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs mb-1">Annual Volume</p>
                          <p className="text-slate-700">{detailItem.annual_volume ? detailItem.annual_volume.toLocaleString() : <span className="text-amber-500">Not set</span>}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs mb-1">Est. Annual Impact</p>
                          <p className="text-slate-700 font-semibold">{detailItem.estimated_annual_impact != null ? `€${detailItem.estimated_annual_impact.toLocaleString()}` : '—'}</p>
                        </div>
                        <div className="flex items-end">
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => setEditItem({...detailItem})} data-testid="edit-item-btn">
                            <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Alerts History */}
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <h4 className="text-sm font-semibold text-slate-700 mb-4">Change History</h4>
                    {detailAlerts.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-6">No changes detected yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {detailAlerts.map(alert => (
                          <div key={alert.alert_id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`alert-${alert.alert_id}`}>
                            <div className={`p-1.5 rounded-lg mt-0.5 ${alert.duty_delta > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                              {alert.duty_delta > 0 ? <ArrowUpRight className="w-4 h-4 text-red-600" /> : <ArrowDownRight className="w-4 h-4 text-emerald-600" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-slate-700">
                                  Duty: {alert.duty_before}% → {alert.duty_after}%
                                  <span className={`ml-2 text-xs ${alert.duty_delta > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                    ({alert.duty_delta > 0 ? '+' : ''}{alert.duty_delta}%)
                                  </span>
                                </p>
                                <span className="text-xs text-slate-400">{new Date(alert.created_at).toLocaleDateString()}</span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">{alert.measure_type} — {alert.legal_reference}</p>
                              {alert.estimated_impact > 0 && (
                                <p className="text-xs font-medium text-amber-600 mt-1">Impact: €{alert.estimated_impact.toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar actions */}
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Quick Actions</h4>
                    <div className="space-y-2">
                      <Button className="w-full justify-start bg-[#1E40AF] hover:bg-[#1E3A8A] text-white" size="sm" onClick={() => handleCheckSingle(detailItem.item_id)}>
                        <RefreshCw className="w-4 h-4 mr-2" /> Check Now (2 credits)
                      </Button>
                      <Button variant="outline" className="w-full justify-start" size="sm" onClick={() => setEditItem({...detailItem})}>
                        <Edit2 className="w-4 h-4 mr-2" /> Edit Item
                      </Button>
                      <Button variant="outline" className="w-full justify-start text-red-500 hover:bg-red-50 hover:text-red-600" size="sm" onClick={() => { handleDelete(detailItem.item_id, detailItem.item_name); setActiveTab('watchlist'); setDetailItem(null); }}>
                        <Trash2 className="w-4 h-4 mr-2" /> Remove Item
                      </Button>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">Metadata</h4>
                    <div className="space-y-2 text-xs text-slate-500">
                      <div className="flex justify-between"><span>Created</span><span>{new Date(detailItem.created_at).toLocaleDateString()}</span></div>
                      <div className="flex justify-between"><span>Last Checked</span><span>{detailItem.last_checked_at ? new Date(detailItem.last_checked_at).toLocaleDateString() : '—'}</span></div>
                      <div className="flex justify-between"><span>Last Change</span><span>{detailItem.last_change_detected_at ? new Date(detailItem.last_change_detected_at).toLocaleDateString() : '—'}</span></div>
                      <div className="flex justify-between"><span>HS6 (Normalized)</span><span className="font-mono">{detailItem.normalized_hs6_code}</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ALERTS TAB */}
          {activeTab === 'alerts' && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="alerts-panel">
              {alerts.length === 0 ? (
                <div className="p-12 text-center">
                  <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Alerts Yet</h3>
                  <p className="text-sm text-slate-400">When duty changes are detected, they&apos;ll appear here.</p>
                </div>
              ) : (
                <div>
                  {alerts.map(alert => (
                    <div key={alert.alert_id} className={`flex items-start gap-4 p-4 border-b border-slate-50 hover:bg-slate-50/50 ${!alert.read ? 'bg-blue-50/30' : ''}`} data-testid={`alert-row-${alert.alert_id}`}>
                      <div className={`p-2 rounded-lg ${alert.duty_delta > 0 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                        {alert.duty_delta > 0 ? <ArrowUpRight className="w-5 h-5 text-red-600" /> : <ArrowDownRight className="w-5 h-5 text-emerald-600" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-slate-700">{alert.item_name}</p>
                          <span className="text-xs text-slate-400">{new Date(alert.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded font-mono mr-2">{alert.hs_code}</code>
                          {alert.origin_country} → {alert.import_market}: {alert.duty_before}% → {alert.duty_after}%
                          <span className={`ml-1 font-medium ${alert.duty_delta > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            ({alert.duty_delta > 0 ? '+' : ''}{alert.duty_delta}%)
                          </span>
                        </p>
                        {alert.estimated_impact > 0 && (
                          <p className="text-xs text-amber-600 font-medium mt-1">Estimated impact: €{alert.estimated_impact.toLocaleString()}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{alert.measure_type} — {alert.legal_reference}</p>
                      </div>
                      <button onClick={() => handleViewDetail(alert.item_id)} className="p-2 text-slate-400 hover:text-[#1E40AF] hover:bg-blue-50 rounded-lg" title="View item">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Edit modal */}
          {editItem && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
              <div className="bg-white rounded-2xl p-6 w-[480px] shadow-xl">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-semibold text-slate-800">Edit Item</h3>
                  <button onClick={() => setEditItem(null)}><X className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Item Name</label>
                    <input value={editItem.item_name || ''} onChange={e => setEditItem({...editItem, item_name: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Unit Value</label>
                      <input type="number" step="0.01" value={editItem.unit_value || ''} onChange={e => setEditItem({...editItem, unit_value: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Annual Volume</label>
                      <input type="number" value={editItem.annual_volume || ''} onChange={e => setEditItem({...editItem, annual_volume: e.target.value})} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
                    <Button className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white" onClick={handleUpdateItem}>Save Changes</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DutyRiskMonitoring;
